import Layout from "@/components/layout/layout"
import { MobileNotSupported } from "@/components/mobile-not-supported"
import { DialogProvider } from "@/components/providers/dialog-provider"
import { QueryProvider } from "@/components/providers/query-provider"
import { ThemeProvider } from "@/components/providers/theme-provider"
import { Toaster } from "@/components/providers/toaster"
import { FloatingTimer } from '@/components/timer/floating-timer'

import { TimerProvider } from '@/contexts/timer-context'
import { AuthProvider, ProtectedRoute, useAuth } from "@/features/auth"
import { useIsMobile } from "@/hooks/use-mobile"

import AIChatPage from "@/pages/ai-chat/page"
import AuthenticationPage from "@/pages/authentication/page"
import ChapterPage from "@/pages/chapters/page"
import EntityCreatePage from "@/pages/entity-create/page"
import EntityDetailsPage from "@/pages/entity-details/page"
import OverviewPage from "@/pages/overview/page"
import TagsPage from "@/pages/tags/page"
import ThoughtsPage from "@/pages/thoughts/page"
import TimeLinePage from "@/pages/timeline/page"
import { RegisterDialogs } from './components/dialogs/register-dialogs'
import { NetworkErrorPage } from "./components/network-error-page"

import WorkspacePage from "@/pages/workspace/page"
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { EchoRuntimeProvider } from '@/pages/ai-chat/remote-threadlist-adapter'
import { useApp } from '@/contexts/app-context'

import NotePage from './components/pages/notes/note-page'
import AccountPage from './pages/account/page'
import { TemplateEditorPage, TemplateListPage } from './pages/form-builder'
import MindMapPage from './pages/mind-maps/page'
import ProgressTrackerPage from './pages/progress-tracker/page'
import SettingsPage from './pages/settings/page'
import TemplateManagerPage from './pages/template-manager/page'


import { ElementsCategoryPage } from './pages/elements'
import ElementsOverviewPage from './pages/elements/elements-overview-page'
import OnboardingFlowPage from './pages/onboarding-flow/page'
import ChapterScenePlannerPage from './pages/chapter-scene-planner/page'

import { TypingEffectsTest } from '@/components/editor/typing-effects/typing-effects-test'
import NotesPage from '@/pages/notes/page'
import { useOnlineStatus } from "./hooks/use-online-status"
import AiPromptsPage from "./pages/ai-prompts/page"

/**
 * ProjectRedirect component to redirect to overview
 */
const ProjectRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}/overview`} replace />;
};

/**
 * AppRoutes component that handles all application routes
 * and implements authentication protection
 */
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { tenantId, isLoading: isAppLoading } = useApp();
  const isMobile = useIsMobile();

  // Show mobile not supported message on mobile devices
  if (isMobile) {
    return <MobileNotSupported />;
  }

  // 1. Always wait for auth status
  // 2. If authenticated, also wait for app metadata (tenant, etc) to be fully loaded AND populated
  const isWaitingForMeta = isAuthenticated && (isAppLoading || !tenantId);

  if (isAuthLoading || isWaitingForMeta) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background">
        <div className="flex flex-col items-center gap-2">
          <div className="text-xl font-medium animate-pulse text-foreground">Loading cognito</div>
          <div className="text-sm text-muted-foreground animate-pulse">Setting up your creative workspace...</div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Redirect to workspace if logged in, otherwise to auth page */}
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? "/workspace" : "/auth"} replace />}
      />

      {/* Authentication page - redirects to workspace if already logged in */}
      <Route
        path="/auth"
        element={isAuthenticated ? <Navigate to="/workspace" replace /> : <AuthenticationPage />}
      />

      {/* Protected routes that require authentication */}
      <Route
        path="/workspace"
        element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>}
      />

      {/* Project routes */}
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <Layout>
              <ProjectRedirect />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Project routes */}
      <Route
        path="/projects/:projectId/overview"
        element={
          <ProtectedRoute>
            <Layout><OverviewPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings route */}
      <Route
        path="/projects/:projectId/settings"
        element={
          <ProtectedRoute>
            <Layout><SettingsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Project feature routes */}
      <Route
        path="/projects/:projectId/timeline"
        element={
          <ProtectedRoute>
            <Layout><TimeLinePage /></Layout>
          </ProtectedRoute>
        }
      />



      <Route
        path="/projects/:projectId/chapters"
        element={
          <ProtectedRoute>
            <Layout><ChapterPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects/:projectId/chapters/plan-scenes"
        element={
          <ProtectedRoute>
            <Layout><ChapterScenePlannerPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects/:projectId/chapters/:chapterId"
        element={
          <ProtectedRoute>
            <Layout><ChapterPage /></Layout>
          </ProtectedRoute>
        }
      />



      <Route
        path="/chapters/:chapterId"
        element={
          <ProtectedRoute>
            <Layout><ChapterPage /></Layout>
          </ProtectedRoute>
        }
      />



      {/* Tags route */}
      <Route
        path="/projects/:projectId/tags"
        element={
          <ProtectedRoute>
            <Layout><TagsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Progress Tracker route */}
      <Route
        path="/projects/:projectId/progress"
        element={
          <ProtectedRoute>
            <Layout><ProgressTrackerPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Entity routes */}


      <Route
        path="/projects/:projectId/entity/:entityId"
        element={
          <ProtectedRoute>
            <Layout><EntityDetailsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Add the correct route for entities (plural) */}
      <Route
        path="/projects/:projectId/entities/:entityId"
        element={
          <ProtectedRoute>
            <Layout><EntityDetailsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Form Builder route */}
      <Route
        path="/projects/:projectId/form-builder"
        element={
          <ProtectedRoute>
            <Layout><TemplateListPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Form Builder Editor route */}
      <Route
        path="/projects/:projectId/form-builder/editor"
        element={
          <ProtectedRoute>
            <Layout><TemplateEditorPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Test route for form builder editor */}


      {/* Test route for typing effects */}
      <Route
        path="/typing-effects-test"
        element={
          <ProtectedRoute>
            <Layout>
              <div className="flex h-full items-center justify-center p-6">
                <TypingEffectsTest />
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Template Manager route */}
      <Route
        path="/projects/:projectId/templates"
        element={
          <ProtectedRoute>
            <Layout><TemplateManagerPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Template Editor route */}
      <Route
        path="/projects/:projectId/templates/:templateId"
        element={
          <ProtectedRoute>
            <Layout><TemplateEditorPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Notes routes */}
      <Route
        path="/projects/:projectId/notes"
        element={
          <ProtectedRoute>
            <Layout>
              <NotesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects/:projectId/notes/:noteId"
        element={
          <ProtectedRoute>
            <Layout><NotePage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Standalone notes route for direct access */}
      <Route
        path="/notes/:noteId"
        element={
          <ProtectedRoute>
            <Layout><NotePage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Thoughts route */}
      <Route
        path="/projects/:projectId/thoughts"
        element={
          <ProtectedRoute>
            <Layout><ThoughtsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* AI Chat routes - with optional conversation ID */}
      <Route
        path="/projects/:projectId/ai-chat"
        element={
          <ProtectedRoute>
            <Layout>
              <AIChatPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/ai-chat/:conversationId"
        element={
          <ProtectedRoute>
            <Layout>
              <AIChatPage />
            </Layout>
          </ProtectedRoute>
        }
      />



      {/* Elements routes */}
      <Route
        path="/projects/:projectId/elements"
        element={
          <ProtectedRoute>
            <Layout>
              <ElementsOverviewPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/projects/:projectId/elements/:categoryId"
        element={
          <ProtectedRoute>
            <Layout><ElementsCategoryPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Entities routes */}
      <Route
        path="/projects/:projectId/entities"
        element={
          <ProtectedRoute>
            <Layout><EntityCreatePage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Mind Maps routes */}
      <Route
        path="/projects/:projectId/mind-maps"
        element={
          <ProtectedRoute>
            <Layout><MindMapPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Project-level mind map routes */}
      <Route
        path="/projects/:projectId/mind-maps/:id"
        element={
          <ProtectedRoute>
            <Layout><MindMapPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* AI Prompts route */}
      <Route
        path="/projects/:projectId/ai-prompts"
        element={
          <ProtectedRoute>
            <Layout><AiPromptsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Standalone mind map routes */}
      <Route
        path="/mind-maps/:id"
        element={
          <ProtectedRoute>
            <Layout><MindMapPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Account page route - standalone without sidebar */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />



      {/* Onboarding flow route - standalone without sidebar */}
      <Route
        path="/onboarding-flow"
        element={
          <ProtectedRoute>
            <OnboardingFlowPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

/**
 * Main App component that sets up the router and providers
 */
const App: React.FC = () => {
  useEffect(() => {
    // Add global styles to ensure proper scrolling
    document.documentElement.style.height = '100%';
    document.documentElement.style.overflowY = 'visible !important';
    document.body.style.height = '100%';
    document.body.style.overflowY = 'scroll !important';
    document.body.style.margin = '0';
    document.body.style.padding = '0';

    return () => {
      // Cleanup styles if needed
      document.documentElement.style.height = '';
      document.documentElement.style.overflowY = '';
      document.body.style.height = '';
      document.body.style.overflowY = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, []);

  const isOnline = useOnlineStatus();

  return (
    <ThemeProvider defaultTheme="system">
      <QueryProvider>
        <BrowserRouter>
          <AuthProvider>
            <TimerProvider>
              <DialogProvider>
                {!isOnline && <NetworkErrorPage />}
                <RegisterDialogs />
                <EchoRuntimeProvider>
                  <AppRoutes />
                </EchoRuntimeProvider>
                <FloatingTimer />
                <Toaster />
              </DialogProvider>
            </TimerProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryProvider>
    </ThemeProvider>
  );
};

export default App
